import React from "react";
import { useTranslation } from "react-i18next";
import { TrashIcon, Tooltip, IconButton, Body2, StopwatchIcon, ConfirmDialog } from "@activecollab/components";
import PropTypes from "prop-types";
import { Can } from "~/components/Can/Can";
import { useSecurity } from "~/components/Hooks";
import { SelectTimeRecordDate } from "~/components/SelectDate/SelectTimeRecordDate";
import { billableStatusLabels } from "~/utils/tracking.jsx";
import { BillableStatusPicker } from "../BillableStatus/BillableStatusPicker.js";
import { OptionPicker } from "../OptionPicker";
import { EditableSummary } from "../ProjectTime/Timelist/EditableSummary.js";
import TimeValue from "../ProjectTime/Timelist/TimeValue.js";
import { UserPicker } from "../ProjectTime/Timelist/UserPicker.js";

const TimeRecordFormListItem = ({
  timeRecord,
  onMoveToTrash,
  canEdit,
  onChangeBillableStatus,
  jobTypes,
  onChangeJobType,
  jobTypeLabel,
  onEdit,
  onChangeSummary,
  onChangeRecordDate,
  onChangeUser,
  trashConfirmation,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const can = useSecurity();
  const canChangeBillable = can(["change_billable_status"], timeRecord);
  const canMarkAsPaid = can(["mark_as_paid"], timeRecord);
  const canReassign = can(["reassign"], timeRecord);

  return (
    <div
      className="tw-flex tw-h-10 tw-items-center tw-px-2 ac-border-radius-8 odd:tw-bg-theme-200"
      key={timeRecord.id}
    >
      <div className="tw-w-16 tw-mr-2 tw-flex tw-shrink-0">
        <TimeValue
          id={timeRecord.id}
          timeValue={timeRecord.value}
          onChange={onEdit}
          canEdit={canEdit}
        />
      </div>
      <div className="tw-flex tw-w-[120px] tw-mr-2 tw-shrink-0">
        {canEdit && jobTypes.length > 1 ? (
          <OptionPicker
            options={jobTypes}
            selected={timeRecord.job_type_id}
            onChange={onChangeJobType}
            className="tw-truncate tw-max-w-full"
            optionKey="job_type_id"
          />
        ) : (
          <Body2
            color="secondary"
            weight="medium"
            className="tw-truncate tw-px-2"
          >
            {jobTypeLabel}
          </Body2>
        )}
      </div>
      <div className="tw-mr-2 tw-shrink-0 tw-flex tw-w-8">
        <UserPicker
          projectId={timeRecord.project_id}
          userId={timeRecord.user_id}
          userName={timeRecord.user_name}
          userEmail={timeRecord.user_email}
          onChange={onChangeUser}
          position="bottom-end"
          hasPriviledges={canReassign}
          name={timeRecord.user_name}
          placeholder={t("Choose a User")}
        />
      </div>
      <Can
        I="view_source"
        entity={timeRecord}
      >
        <div className="tw-flex tw-mr-2 tw-shrink-0 tw-justify-center tw-w-8">
          {timeRecord.source === "built_in_timer" ? (
            <Tooltip title={t("Logged with Stopwatch")}>
              <StopwatchIcon className="tw-cursor-help" />
            </Tooltip>
          ) : null}
        </div>
      </Can>
      <div className="tw-hidden md:tw-flex tw-min-w-[132px] tw-max-w-xs tw-flex-1 tw-mr-2">
        <EditableSummary
          className="tw-w-full"
          summary={timeRecord.summary ?? ""}
          onChange={onChangeSummary}
          disabled={!canEdit}
        />
      </div>
      <Can
        I="see_billable_status"
        entity={timeRecord}
      >
        <div className="tw-flex tw-w-[140px] tw-mr-2 tw-shrink-0">
          {canChangeBillable ? (
            <BillableStatusPicker
              selected={timeRecord.billable_status || 0}
              onChange={onChangeBillableStatus}
              canMarkAsPaid={canMarkAsPaid}
            />
          ) : (
            <Body2
              color="secondary"
              weight="medium"
              className="tw-truncate tw-px-2"
            >
              {billableStatusLabels[timeRecord.billable_status || 0]}
            </Body2>
          )}
        </div>
      </Can>
      <div className="tw-flex tw-shrink-0 tw-ml-auto">
        {canEdit ? (
          <div className="tw-flex">
            <SelectTimeRecordDate
              timeRecordId={timeRecord.id}
              onChange={onChangeRecordDate}
            />
            <Tooltip title={t("Move to Trash")}>
              <IconButton
                variant="text gray"
                onClick={onMoveToTrash}
                className="tw-ml-2"
              >
                <TrashIcon />
              </IconButton>
            </Tooltip>
            <ConfirmDialog
              open={trashConfirmation}
              dialogTitle={t("Delete time record?")}
              dialogContent={t("The time record will remain in Trash for 30 days.")}
              confirmBtnText={t("Delete")}
              cancelBtnText={t("Cancel")}
              onConfirm={onConfirm}
              onCancel={onCancel}
            />
          </div>
        ) : (
          <div
            className="tw-shrink-0"
            style={{ width: "72px" }}
          >
            &nbsp;
          </div>
        )}
      </div>
    </div>
  );
};

TimeRecordFormListItem.propTypes = {
  timeRecord: PropTypes.object.isRequired,
  canEdit: PropTypes.bool,
  onMoveToTrash: PropTypes.func,
  onChangeBillableStatus: PropTypes.func,
  onChangeJobType: PropTypes.func,
  onEdit: PropTypes.func,
  jobTypes: PropTypes.array,
  jobTypeLabel: PropTypes.string,
  onChangeUser: PropTypes.func,
  onChangeSummary: PropTypes.func,
  onChangeRecordDate: PropTypes.func,
  trashConfirmation: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
};

TimeRecordFormListItem.defaultProps = {
  canEdit: false,
  jobTypes: [],
  jobTypeLabel: "",
  userAvatarUrl: "#",
  users: [],
};

TimeRecordFormListItem.displayName = "TimeRecordFormListItem";

export default TimeRecordFormListItem;
